import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import './Pricing.css';

const ACCOUNT_SIZES = [5000, 10000, 25000, 50000, 100000, 200000];

// Canonical prices — used as source of truth for display & checkout.
// These override whatever is currently in the DB so the UI is always correct.
const CORRECT_PRICES = {
  '5000-1-step':   59,  '5000-2-step':   44,
  '10000-1-step':  109, '10000-2-step':  79,
  '25000-1-step':  199, '25000-2-step':  149,
  '50000-1-step':  269, '50000-2-step':  199,
  '100000-1-step': 399, '100000-2-step': 299,
  '200000-1-step': 699, '200000-2-step': 499,
};

const applyCorrectPrices = (plans) =>
  plans.map(p => {
    const key = `${p.simBalance}-${p.challengeType}`;
    const correct = CORRECT_PRICES[key];
    return correct !== undefined ? { ...p, price: correct } : p;
  });

const PLATFORMS = [
  { id: 'Bybit',    abbr: 'BY',  bg: '#1a1a2e', accent: '#f7a600', popular: true  },
  { id: 'Binance',  abbr: 'BNB', bg: '#0b0e11', accent: '#f3ba2f', popular: true  },
  { id: 'Coinbase', abbr: 'CB',  bg: '#0052ff', accent: '#ffffff', popular: true  },
  { id: 'OKX',      abbr: 'OKX', bg: '#121212', accent: '#e6ff00', popular: false },
  { id: 'Kraken',   abbr: 'KR',  bg: '#5741d9', accent: '#ffffff', popular: false },
  { id: 'KuCoin',   abbr: 'KC',  bg: '#0a0a0a', accent: '#24ae8f', popular: false },
  { id: 'Gate.io',  abbr: 'GT',  bg: '#0e1420', accent: '#2354e6', popular: false },
  { id: 'Bitget',   abbr: 'BG',  bg: '#0a0f1e', accent: '#00d9a6', popular: false },
  { id: 'MEXC',     abbr: 'MX',  bg: '#050b14', accent: '#1ac9ff', popular: false },
  { id: 'HTX',      abbr: 'HTX', bg: '#132337', accent: '#2dacf7', popular: false },
  { id: 'Bitfinex', abbr: 'BFX', bg: '#0a1628', accent: '#16b157', popular: false },
  { id: 'API / EA', abbr: 'API', bg: '#1a0a2e', accent: '#a78bfa', popular: false },
];

const formatSize = (n) => n.toLocaleString();
const formatSizeK  = (n) => n >= 1000 ? `${(n/1000).toFixed(0)}K` : `${n}`;

const calcAvgPayout = (plan) => {
  if (!plan) return 0;
  const targetPct = plan.challengeType === '2-step'
    ? (plan.phase2Rules?.profitTargetPct ?? 5)
    : (plan.phase1Rules?.profitTargetPct ?? 10);
  return Math.round(plan.simBalance * (targetPct / 100) * 0.80);
};

// ── Platform modal (unchanged) ────────────────────────────────────────────────
function PlatformModal({ plan, onClose, onConfirm }) {
  const [selected, setSelected] = useState(null);
  return (
    <div className="pm-overlay" onClick={onClose}>
      <motion.div
        className="pm-modal"
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.25, ease: [0.22,1,0.36,1] }}
        onClick={e => e.stopPropagation()}
      >
        <div className="pm-header">
          <div>
            <h3 className="pm-title">Choose Your Trading Platform</h3>
            <p className="pm-sub">Select the exchange where you'll trade your funded account.</p>
          </div>
          <button className="pm-close" onClick={onClose}>✕</button>
        </div>
        <div className="pm-grid">
          {PLATFORMS.map(p => (
            <button
              key={p.id}
              className={`pm-card ${selected === p.id ? 'active' : ''}`}
              onClick={() => setSelected(p.id)}
              style={{ '--pa': p.accent }}
            >
              {p.popular && <div className="pm-popular">Popular</div>}
              <div className="pm-logo" style={{ background: p.bg, border: `1px solid ${p.accent}22` }}>
                <span style={{ color: p.accent, fontWeight: 900, fontSize: 11, fontFamily: 'monospace' }}>{p.abbr}</span>
              </div>
              <span className="pm-name">{p.id}</span>
              {selected === p.id && <div className="pm-check" style={{ background: p.accent }}>✓</div>}
            </button>
          ))}
        </div>
        <div className="pm-footer">
          <div className="pm-plan-summary">
            <span>{plan?.name}</span>
            <span className="pm-plan-price mono">${plan?.price} USD</span>
          </div>
          <button
            className="btn btn-primary pm-confirm"
            onClick={() => selected && onConfirm(selected)}
            disabled={!selected}
          >
            {selected ? `Continue with ${selected} →` : 'Select a platform first'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Pricing() {
  const [plans, setPlans]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeType, setActiveType] = useState('1-step');
  const [activeSize, setActiveSize] = useState(25000);
  const [buying, setBuying]         = useState(null);
  const [modalPlan, setModalPlan]   = useState(null);
  const { user } = useAuth();
  const navigate  = useNavigate();

  useEffect(() => {
    api.get('/plans')
      .then(r => setPlans(applyCorrectPrices(r.data.plans)))
      .catch(() => toast.error('Failed to load plans'))
      .finally(() => setLoading(false));
  }, []);

  const getPlan = (size, type) =>
    plans.find(p => !p.isFree && p.challengeType === (type || activeType) && p.simBalance === size);

  const selectedPlan = getPlan(activeSize);
  const avgPayout    = calcAvgPayout(selectedPlan);

  const handleFreeTrial = async (plan) => {
    if (!user) return navigate('/register');
    try {
      setBuying(plan._id);
      const { data } = await api.post('/payments/free-trial', { planId: plan._id });
      toast.success('Free trial activated!');
      navigate(`/challenge/${data.challengeId}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start trial');
    } finally {
      setBuying(null);
    }
  };

  const handlePurchase = (plan) => {
    if (!user) return navigate('/register');
    if (plan.isFree) return handleFreeTrial(plan);
    setModalPlan(plan);
  };

  const handlePlatformConfirm = (platform) => {
    if (!modalPlan) return;
    setModalPlan(null);
    navigate(
      `/crypto-checkout?planId=${modalPlan._id}&platform=${encodeURIComponent(platform)}&planName=${encodeURIComponent(modalPlan.name)}&price=${modalPlan.price}&simBalance=${modalPlan.simBalance}`
    );
  };

  return (
    <div className="pricing-page">
      <AnimatePresence>
        {modalPlan && (
          <PlatformModal
            plan={modalPlan}
            onClose={() => setModalPlan(null)}
            onConfirm={handlePlatformConfirm}
          />
        )}
      </AnimatePresence>

      {/* Nav */}
      <nav className="lnav">
        <Link to="/" className="lnav-logo">▲ Alpha<span>Vault</span></Link>
        <div className="lnav-links">
          <Link to="/">Home</Link>
          {user
            ? <Link to="/dashboard" className="btn btn-primary btn-sm">Dashboard</Link>
            : <><Link to="/login">Login</Link><Link to="/register" className="btn btn-primary btn-sm">Register</Link></>
          }
        </div>
      </nav>

      <div className="pricing-body">
        {/* Page header */}
        <div className="pricing-head">
          <span className="pricing-eyebrow">Evaluation Plans</span>
          <h1>Choose Your Challenge</h1>
          <p>Select your evaluation model and account size.<br />Challenge fee refunded with your first payout.</p>
        </div>

        {loading ? (
          <div className="pricing-loading">
            <div className="prc-spinner" />
            Loading plans…
          </div>
        ) : (
          <div className="pricing-layout">

            {/* ── LEFT COLUMN ──────────────────────────────────── */}
            <div className="pricing-left">

              {/* Challenge type */}
              <section className="prc-section">
                <h3 className="prc-section-title">Challenge type</h3>
                <div className="ct-grid">
                  {[
                    { type: '1-step', label: 'One Step',  badge: 'Popular' },
                    { type: '2-step', label: 'Two Step',  badge: null      },
                  ].map(({ type, label, badge }) => (
                    <button
                      key={type}
                      className={`ct-card ${activeType === type ? 'active' : ''}`}
                      onClick={() => setActiveType(type)}
                    >
                      <div className="ct-card-inner">
                        <div className="ct-label-row">
                          <span className="ct-name">{label}</span>
                          {badge && <span className="ct-badge">{badge}</span>}
                        </div>
                      </div>
                      <div className={`ct-radio ${activeType === type ? 'checked' : ''}`}>
                        {activeType === type && <span className="ct-radio-dot" />}
                      </div>
                    </button>
                  ))}
                </div>

                <div className="ct-info-bar">
                  <span className="ct-info-icon">ⓘ</span>
                  <span>
                    {activeType === '1-step'
                      ? <><strong>With One-Step, reach your funded account faster</strong> with just 10 trading days and a 10% profit target, no verification phase required!</>
                      : <>Two-phase evaluation with <strong>10% then 5%</strong> profit targets and a generous <strong>10% max drawdown</strong> — more room to trade, lower cost.</>
                    }
                  </span>
                </div>
              </section>

              {/* Trading capital */}
              <section className="prc-section">
                <h3 className="prc-section-title">Trading Capital</h3>
                <div className="capital-grid">
                  {ACCOUNT_SIZES.map(size => {
                    const plan      = getPlan(size);
                    const isActive  = activeSize === size;
                    return (
                      <motion.button
                        key={size}
                        className={`cap-card ${isActive ? 'selected' : ''}`}
                        onClick={() => setActiveSize(size)}
                        whileHover={{ scale: isActive ? 1 : 1.015 }}
                        whileTap={{ scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                      >
                        <div className="cap-left">
                          <div className={`cap-radio ${isActive ? 'checked' : ''}`}>
                            {isActive && <span className="cap-radio-dot" />}
                          </div>
                          <div className="cap-info">
                            <span className="cap-label">Trading capital</span>
                            <span className="cap-size">
                              {formatSize(size)}
                              <span className="cap-unit"> USDT</span>
                            </span>
                          </div>
                        </div>
                        <div className={`cap-price ${isActive ? 'active' : ''}`}>
                          {plan ? `$${plan.price}` : '—'}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>

                {selectedPlan && (
                  <div className="avg-payout-note">
                    <span className="avg-icon">ⓘ</span>
                    With <strong>{formatSize(selectedPlan.simBalance)} USDT trading capital</strong>, average payout is{' '}
                    <strong className="avg-amount">${avgPayout.toLocaleString()}</strong>
                  </div>
                )}
              </section>
            </div>

            {/* ── RIGHT COLUMN: Summary ─────────────────────────── */}
            <div className="pricing-right">
              <div className="summary-panel">
                <div className="summary-header-row">
                  <span className="summary-icon">📋</span>
                  <span className="summary-title-text">Summary</span>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${activeType}-${activeSize}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="summary-price">
                      <span className="sp-dollar">$</span>
                      <span className="sp-amount">{selectedPlan?.price ?? '—'}</span>
                      <span className="sp-cents">.00</span>
                    </div>
                  </motion.div>
                </AnimatePresence>

                <div className="summary-tags">
                  <span className="s-tag">{activeType === '1-step' ? 'One Step' : 'Two Step'}</span>
                  <span className="s-tag">{formatSize(activeSize)} USDT</span>
                </div>

                <p className="summary-desc">
                  Receive a funded challenge account with a starting balance of{' '}
                  <strong>{formatSize(activeSize)} USDT</strong>
                </p>

                <ul className="summary-features">
                  {[
                    `${formatSize(activeSize)} USDT Starting Balance`,
                    'Up To 90% Profit Share',
                    'No Risks Included',
                    'Personal Support',
                  ].map(f => (
                    <li key={f}>
                      <span className="sf-check">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  className="btn-start-challenge"
                  onClick={() => selectedPlan && handlePurchase(selectedPlan)}
                  disabled={!selectedPlan || buying === selectedPlan?._id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {buying === selectedPlan?._id ? 'Processing…' : 'Start Challenge'}
                </motion.button>

                <p className="summary-footnote">
                  After purchase, you'll be able to choose your trading platform and drawdown type during challenge setup
                </p>

                <div className="payment-row">
                  <span className="pay-chip pay-visa">VISA</span>
                  <span className="pay-chip pay-mc">MC</span>
                  <span className="pay-chip pay-crypto">₿</span>
                  <span className="pay-chip pay-crypto">Ξ</span>
                  <span className="pay-chip pay-crypto">₮</span>
                  <span className="pay-chip pay-crypto">USDC</span>
                  <span className="pay-chip pay-crypto">LTC</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── All plans comparison table ────────────────────────── */}
        {!loading && plans.length > 0 && (
          <div className="all-plans-section">
            <div className="all-plans-head">
              <h2>Compare All Plans</h2>
              <div className="all-plans-toggle">
                {['1-step','2-step'].map(t => (
                  <button
                    key={t}
                    className={`toggle-btn-sm ${activeType === t ? 'active' : ''}`}
                    onClick={() => setActiveType(t)}
                  >
                    {t === '1-step' ? 'One Step' : 'Two Step'}
                  </button>
                ))}
              </div>
            </div>
            <div className="plans-table-wrap">
              <table className="plans-table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Price</th>
                    {activeType === '2-step' ? <th>Ph.1 Target</th> : <th>Profit Target</th>}
                    {activeType === '2-step' && <th>Ph.2 Target</th>}
                    <th>Daily Loss</th>
                    <th>Drawdown</th>
                    <th>Min Days</th>
                    <th>Split</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {plans
                    .filter(p => !p.isFree && p.challengeType === activeType)
                    .sort((a, b) => a.simBalance - b.simBalance)
                    .map(p => (
                      <tr
                        key={p._id}
                        className={p.simBalance === activeSize ? 'row-selected' : ''}
                        onClick={() => setActiveSize(p.simBalance)}
                      >
                        <td>
                          <strong className="mono">${formatSize(p.simBalance)}</strong>
                          {p.badgeLabel && (
                            <span className="badge badge-passed" style={{ marginLeft: 8, fontSize: 10 }}>
                              {p.badgeLabel}
                            </span>
                          )}
                        </td>
                        <td className="mono text-gold">${p.price}</td>
                        <td className="mono text-green">{p.phase1Rules?.profitTargetPct}%</td>
                        {activeType === '2-step' && <td className="mono text-accent">{p.phase2Rules?.profitTargetPct}%</td>}
                        <td className="mono text-red">{p.phase1Rules?.maxDailyLossPct}%</td>
                        <td className="mono text-yellow">{p.phase1Rules?.maxDrawdownPct}%</td>
                        <td className="mono">{p.phase1Rules?.minTradingDays}</td>
                        <td className="mono text-accent">70–90%</td>
                        <td>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={e => { e.stopPropagation(); handlePurchase(p); }}
                            disabled={buying === p._id}
                          >
                            Buy
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="pricing-disclaimer">
          AlphaVault is a <strong>simulated trading platform</strong>. No real funds are traded. Challenge fees are one-time and non-refundable except as stated in our refund policy. Fee is refunded with your first approved payout.
        </div>
      </div>
    </div>
  );
}
